/**
 * Super Admin discount codes. The deploy's Stripe secret key chooses the
 * mode: sk_live_ in production, sk_test_ on dev and branch deploys.
 * PACARIONBETA2026 is an existing promotion code and is never deleted.
 */

const PLAN_OPTIONS = [
  { id: 'solo', label: 'Solo' },
  { id: 'team', label: 'Team' },
  { id: 'plus', label: 'Plus' },
  { id: 'max', label: 'Max' },
  { id: 'homeowner', label: 'Homeowner plans' },
];

const KNOWN_CODE = 'PACARIONBETA2026';
const CODE_RE = /^[A-Za-z0-9-]{3,40}$/;
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function stripeKeyKind(value) {
  const key = String(value || '').trim();
  if (key.indexOf('sk_live_') === 0 || key.indexOf('rk_live_') === 0) return 'live';
  if (key.indexOf('sk_test_') === 0 || key.indexOf('rk_test_') === 0) return 'test';
  return '';
}

function discountStripeKey(env = process.env) {
  const source = env || {};
  const secret = String(source.STRIPE_SECRET_KEY || '').trim();
  const secretKind = stripeKeyKind(secret);
  if (secretKind === 'live' || secretKind === 'test') {
    return { key: secret, mode: secretKind, from: 'STRIPE_SECRET_KEY', error: '' };
  }
  const names = ['STRIPE_PULSE_READ_KEY', 'STRIPE_RESTRICTED_KEY'];
  let liveKey = null;
  for (let i = 0; i < names.length; i += 1) {
    const value = String(source[names[i]] || '').trim();
    const kind = stripeKeyKind(value);
    if (kind === 'test') return { key: value, mode: 'test', from: names[i], error: '' };
    if (kind === 'live' && !liveKey) liveKey = { key: value, from: names[i] };
  }
  if (liveKey) return { key: liveKey.key, mode: 'live', from: liveKey.from, error: '' };
  return { key: null, mode: 'off', from: null, error: 'A Stripe key is required.' };
}

function generateDiscountCode(randomInt) {
  const roll = typeof randomInt === 'function'
    ? randomInt
    : (max) => Math.floor(Math.random() * max);
  let code = 'PAC';
  for (let i = 0; i < 6; i += 1) code += ALPHABET.charAt(roll(ALPHABET.length));
  return code;
}

function uniquePlans(plans) {
  const allowed = {};
  PLAN_OPTIONS.forEach((option) => { allowed[option.id] = true; });
  const out = [];
  (Array.isArray(plans) ? plans : []).forEach((id) => {
    const key = String(id || '');
    if (allowed[key] && out.indexOf(key) === -1) out.push(key);
  });
  return out;
}

function moneyCents(dollars) {
  const value = Number(dollars);
  if (!Number.isFinite(value)) return null;
  const cents = Math.round(value * 100);
  if (Math.abs(cents - (value * 100)) > 0.001) return null;
  return cents;
}

function validateDiscountDraft(input, now = new Date()) {
  const source = input || {};
  let code = String(source.code || '').trim();
  if (!code) code = generateDiscountCode(source.randomInt);
  code = code.toUpperCase();
  if (!CODE_RE.test(code)) return { ok: false, error: 'Use 3 to 40 letters, numbers, or dashes.' };
  const type = source.type === 'amount' ? 'amount' : (source.type === 'percent' ? 'percent' : '');
  if (!type) return { ok: false, error: 'Choose a percent or a fixed amount.' };
  const duration = ['once', 'repeating', 'forever'].indexOf(source.duration) === -1 ? '' : source.duration;
  if (!duration) return { ok: false, error: 'Choose how long the discount lasts.' };
  let months = 0;
  if (duration === 'repeating') {
    months = Number(source.months);
    if (!Number.isInteger(months) || months < 1 || months > 36) {
      return { ok: false, error: 'Repeating discounts last 1 to 36 months.' };
    }
  }
  const plans = uniquePlans(source.plans);
  if (!plans.length) return { ok: false, error: 'Select at least one plan.' };
  let percent = 0;
  let cents = 0;
  if (type === 'percent') {
    percent = Number(source.value);
    if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
      return { ok: false, error: 'Percent discounts are a whole number from 1 to 100.' };
    }
  } else {
    cents = moneyCents(source.value);
    if (cents == null || cents < 1 || cents > 1000000) {
      return { ok: false, error: 'Enter a fixed amount from $0.01 to $10,000.' };
    }
  }
  let maxRedemptions = null;
  if (source.maxRedemptions != null && String(source.maxRedemptions).trim() !== '') {
    maxRedemptions = Number(source.maxRedemptions);
    if (!Number.isInteger(maxRedemptions) || maxRedemptions < 1 || maxRedemptions > 100000) {
      return { ok: false, error: 'Max redemptions is a whole number.' };
    }
  }
  let expiresAt = null;
  if (source.expiresOn) {
    const day = String(source.expiresOn).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return { ok: false, error: 'Expiration date is not valid.' };
    const stamp = Date.parse(`${day}T23:59:59.000Z`);
    if (!Number.isFinite(stamp)) return { ok: false, error: 'Expiration date is not valid.' };
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    if (stamp < today) return { ok: false, error: 'Expiration date is in the past.' };
    expiresAt = Math.floor(stamp / 1000);
  }
  return {
    ok: true,
    error: '',
    value: {
      code,
      type,
      percent,
      cents,
      duration,
      months,
      plans,
      maxRedemptions,
      expiresAt,
      firstTime: source.firstTime === true,
    },
  };
}

function couponCreateParams(value, productIds) {
  const params = {
    name: value.code,
    duration: value.duration,
    metadata: {
      plans: value.plans.join(','),
      source: 'super_admin',
    },
  };
  if (value.duration === 'repeating') params.duration_in_months = value.months;
  if (value.type === 'percent') params.percent_off = value.percent;
  else {
    params.amount_off = value.cents;
    params.currency = 'usd';
  }
  const products = (productIds || []).filter(Boolean);
  if (products.length) params.applies_to = { products };
  return params;
}

function promotionCreateParams(value, couponId) {
  const params = {
    promotion: { type: 'coupon', coupon: couponId },
    code: value.code,
    active: true,
    metadata: {
      plans: value.plans.join(','),
      discount_type: value.type,
      discount_value: value.type === 'percent' ? String(value.percent) : String(value.cents),
      duration: value.duration,
      months: value.months ? String(value.months) : '',
      source: 'super_admin',
    },
    restrictions: {},
  };
  if (value.maxRedemptions) params.max_redemptions = value.maxRedemptions;
  if (value.expiresAt) params.expires_at = value.expiresAt;
  if (value.firstTime) params.restrictions.first_time_transaction = true;
  return params;
}

function planLabel(id) {
  const match = PLAN_OPTIONS.find((option) => option.id === id);
  return match ? match.label : id;
}

function discountSummary(coupon, metadata) {
  const meta = metadata || {};
  const percent = coupon && Number.isFinite(Number(coupon.percent_off)) ? Number(coupon.percent_off) : null;
  if (percent) return `${percent}%`;
  if (meta.discount_type === 'percent' && meta.discount_value) return `${meta.discount_value}%`;
  const amount = coupon && Number.isFinite(Number(coupon.amount_off)) ? Number(coupon.amount_off) : null;
  const cents = amount != null ? amount : (meta.discount_type === 'amount' ? Number(meta.discount_value) : null);
  if (cents != null && Number.isFinite(cents)) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
  return 'Discount';
}

function durationSummary(coupon, metadata) {
  const meta = metadata || {};
  const duration = (coupon && coupon.duration) || meta.duration || '';
  const months = Number((coupon && coupon.duration_in_months) || meta.months || 0);
  if (duration === 'once') return 'Once';
  if (duration === 'forever') return 'Forever';
  if (duration === 'repeating' && months) return `${months} month${months === 1 ? '' : 's'}`;
  if (duration === 'repeating') return 'Repeating';
  return '';
}

function promotionCoupon(row) {
  if (!row) return null;
  if (row.coupon && typeof row.coupon === 'object') return row.coupon;
  const promotion = row.promotion || {};
  if (promotion.coupon && typeof promotion.coupon === 'object') return promotion.coupon;
  return null;
}

function promotionCouponId(row) {
  if (!row) return '';
  if (typeof row.coupon === 'string') return row.coupon;
  const promotion = row.promotion || {};
  if (typeof promotion.coupon === 'string') return promotion.coupon;
  return '';
}

function promotionProductIds(row) {
  const coupon = promotionCoupon(row);
  const applies = coupon && coupon.applies_to && coupon.applies_to.products;
  if (!Array.isArray(applies)) return [];
  const ids = [];
  applies.forEach((product) => {
    const id = typeof product === 'string' ? product : (product && product.id);
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
}

function productNamesOf(coupon, names) {
  const applies = coupon && coupon.applies_to && coupon.applies_to.products;
  if (!Array.isArray(applies)) return [];
  const out = [];
  applies.forEach((product) => {
    const id = typeof product === 'string' ? product : (product && product.id);
    if (!id) return;
    if (out.some((item) => item.id === id)) return;
    const embedded = product && typeof product === 'object' ? String(product.name || '') : '';
    const name = (names && names[id]) || embedded || id;
    out.push({ id, name });
  });
  return out;
}

function couponTerms(coupon, metadata) {
  const discount = discountSummary(coupon, metadata);
  const duration = durationSummary(coupon, metadata);
  const off = discount === 'Discount' ? 'Discount' : `${discount} off`;
  if (duration === 'Once') return `${off} once`;
  if (duration === 'Forever') return `${off} forever`;
  if (duration === 'Repeating') return `${off}, repeating`;
  if (duration) return `${off} for ${duration}`;
  return off;
}

function normalizePromotion(row, now = new Date(), productNames) {
  if (!row) return null;
  const coupon = promotionCoupon(row);
  const metadata = row.metadata || {};
  const plans = String((coupon && coupon.metadata && coupon.metadata.plans) || metadata.plans || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  const expiresAt = row.expires_at ? new Date(Number(row.expires_at) * 1000).toISOString() : '';
  const createdAt = row.created ? new Date(Number(row.created) * 1000).toISOString() : '';
  const expired = Boolean(row.expires_at) && (Number(row.expires_at) * 1000) < now.getTime();
  let status = row.active === false ? 'Inactive' : 'Active';
  if (row.active !== false && expired) status = 'Expired';
  const timesRedeemed = Number(row.times_redeemed) || 0;
  const maxRedemptions = Number.isInteger(row.max_redemptions) ? row.max_redemptions : null;
  return {
    id: row.id,
    code: row.code || '',
    active: row.active !== false,
    status,
    livemode: row.livemode === true,
    timesRedeemed,
    timesUsed: timesRedeemed,
    maxRedemptions,
    terms: couponTerms(coupon, metadata),
    discount: discountSummary(coupon, metadata),
    duration: durationSummary(coupon, metadata),
    products: productNamesOf(coupon, productNames),
    plans: plans.map(planLabel),
    expiresAt,
    createdAt,
    firstTime: Boolean(row.restrictions && row.restrictions.first_time_transaction),
    known: String(row.code || '') === KNOWN_CODE,
  };
}

export {
  PLAN_OPTIONS,
  KNOWN_CODE,
  stripeKeyKind,
  discountStripeKey,
  generateDiscountCode,
  validateDiscountDraft,
  couponCreateParams,
  promotionCreateParams,
  promotionCoupon,
  promotionCouponId,
  promotionProductIds,
  normalizePromotion,
  planLabel,
};
